<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable ref="rtable" :settings="{hideTable: true, hideExport: true, title: t('moneyCollectorCommissions')}"
                  :columns="columnDefs"
                  @onDataLoaded="onDataLoaded"
                  endpoint="reports/hr/money-collector-commissions">
      <template #filter>
        <el-select
            filterable
            clearable
            class="col-span-12 md:col-span-3"
            value-key="fullName"
            :placeholder="t('selectMoneyCollector')"
            v-model="selectedMoneyCollector"
            @change="onSelectedMoneyCollectorChanged"
            remote
            :remote-method="searchMoneyCollectors"
        >
          <el-option
              v-for="option in moneyCollectors"
              :key="option.id"
              :label="option.fullName"
              :value="option"
          ></el-option>
        </el-select>
      </template>
      <template #chart="slotProps">
        <div class="grid grid-cols-2 gap-2">
          <div class="col-span-12 md:col-span-2 chart-card" style="height: 300px">
            <v-chart :option="option" autoresize />
          </div>
        </div>
        <el-table :data="slotProps.data" style="width: 100%"
                  header-row-class-name="reports-table-header">
          <el-table-column type="expand">
            <template #default="props">
              <div class="ml-10">
                <el-table :data="props.row.paymentDetails"
                          header-row-class-name="reports-table-header">
                  <el-table-column :label="t('paymentNumber')" prop="paymentNumber"/>
                  <el-table-column :label="t('paymentDateTime')" prop="paymentDate">
                    <template #default="props">
                      <div style="direction: ltr">
                        {{ formatPostgresDate(props.row.paymentDate, 'DD/MM/YYYY hh:mm:ss A') }}
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('orderNumber')" prop="orderNumber"/>
                  <el-table-column :label="t('paymentTotal')" prop="paymentAmount">
                    <template #default="props">
                      {{ formatMoney(props.row.paymentAmount, 'IQD') }}
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('commissionName')" prop="commissionName"/>
                  <el-table-column :label="t('rule')" prop="commissionType">
                    <template #default="props">
                      <div v-if="props.row.commissionType == 'amount'">
                        {{ formatMoney(props.row.commissionValue, 'IQD') }}
                      </div>
                      <div v-if="props.row.commissionType == 'percentage'">
                        {{`${props.row.commissionValue}%`}}
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('commissionValue')" prop="commissionAmount">
                    <template #default="props">
                      {{ formatMoney(props.row.commissionAmount, 'IQD') }}
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="t('moneyCollectorName')" prop="moneyCollectorName"/>
          <el-table-column :label="t('totalPaymentsCollected')"
                           prop="totalPaymentsCollected">
            <template #default="props">
              {{ formatMoney(props.row.totalPaymentsCollected, 'IQD') }}
            </template>
          </el-table-column>
          <el-table-column :label="t('totalCommission')" prop="totalCommission">
            <template #default="props">
              <div class="font-bold text-blue-500">
                {{ formatMoney(props.row.totalCommission, 'IQD') }}
              </div>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import {onBeforeMount, provide, ref} from 'vue'
import {useI18n} from 'vue-i18n'
import {ColDef} from 'ag-grid-community'
import {useHTTP} from '@/plugins/http'
import {formatMoney} from '@/utils/money'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import {formatPostgresDate} from '@/utils/date'
import User from '@/models/user/User'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'

// i18n
const {t} = useI18n({
  messages: {
    en: {
      moneyCollectorCommissions: 'Money Collector Commissions',
      selectMoneyCollector: 'Select Money Collector',
      moneyCollectorName: 'Money Collector Name',
      totalPaymentsCollected: 'Total Payments Collected',
      totalCommission: 'Total Commission',
      paymentNumber: 'Payment Number',
      paymentDateTime: 'Date & Time',
      orderNumber: 'Order Number',
      paymentTotal: 'Payment Total',
      commissionName: 'Commission Name',
      rule: 'Rule',
      commissionValue: 'Commission Value',
    },
    ar: {
      moneyCollectorCommissions: 'عمولات الجباة',
      selectMoneyCollector: 'اختر جابي',
      moneyCollectorName: 'اسم الجابي',
      totalPaymentsCollected: 'إجمالي المبالغ المستحصلة',
      totalCommission: 'إجمالي العمولة',
      paymentNumber: 'رقم الدفعة',
      paymentDateTime: 'التاريخ والوقت',
      orderNumber: 'رقم الطلب',
      paymentTotal: 'إجمالي المستحصل',
      commissionName: 'اسم العمولة',
      rule: 'القاعدة',
      commissionValue: 'قيمة العمولة',
    }
  }
})

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])

const http = useHTTP()


use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  BarChart,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
])

provide(THEME_KEY, 'light')
const option = ref({
  title: {
    left: 'left'
  },
  toolbox: {
    feature: {
      saveAsImage: {}
    }
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow'
    }
  },
  legend: {},
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true
  },

  xAxis: {
    type: 'category',
    data: []
  },
  yAxis: {
    type: 'value',
  },
  series: [
    {
      type: 'bar',
      barWidth: 50,
      itemStyle: {
        color: '#4285F4'
      },
      z: 100,
      data: []
    }
  ]
})

const moneyCollectors = ref<User[] | any>();
const selectedMoneyCollector = ref<any>()

onBeforeMount(async () => {
  columnDefs.value = []
})


async function onSelectedMoneyCollectorChanged() {
  rtable.value.payload.filter = selectedMoneyCollector.value ? selectedMoneyCollector.value.id : null;
  rtable.value.navigate(rtable.value.payload)
}

async function searchMoneyCollectors(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${User.ENDPOINT}?q=${query}`
    const usersRes = await http.get<User[]>(url)
    moneyCollectors.value = usersRes
      .map((user) => User.from(user))
      .filter(user => user.extraProperties?.tags?.includes('Money Collector'))
  } catch (error) {
    moneyCollectors.value = []
  }
}

function onDataLoaded(data: any) {
  const chartData = data.slice(0, 10)
  option.value.series[0].data = chartData.slice().map((i: any) => i.totalCommission)
  option.value.xAxis.data = chartData.slice().map((i: any) => String(i.moneyCollectorName).slice(0, 15))
}

function getDaysTagType(days: number): 'success' | 'warning' | 'danger' | '' {
  if (days <= 3) return 'success'
  if (days <= 7) return 'warning'
  return 'danger'
}
</script>
